/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  IconX,
  IconBell,
  IconSearch,
  IconUser,
  IconBuilding,
  IconSettings,
  IconLogout,
  IconChevronRight,
  IconHistory,
  IconPencil,
  IconBasketCog,
  IconClipboardList,
  IconHeadset,
} from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetch3 } from "../commonApis/commonApis";
import {
  useProfileData,
  useUpdateProfilePicMutation,
} from "../apis/profileApis";
import toast from "react-hot-toast";
import { useFetchAdminNotificationsData } from "../apis/notificationApis";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { BulkResponseHandler } from "./BulkResponseHandler";
import { NotificationItem } from "./NotificaionItem";
import RoleToggle from "./RoleToggle";
import { useGrafanaStatus } from "../apis/dashboardApis";

interface SearchResultItem {
  entityType: "user" | "order" | "client";
  entityId: string;
  displayTitle: string;
  subText?: string;
  awbNumber?: string;
  orderName?: string;
  referenceNumber?: string;
  status?: string;
  createdAt?: string;
}

const getEntityPath = (type: string, id: string): string => {
  switch (type) {
    case "order":
      return `/orders/${id}`;
    case "user":
      return `/users/${id}`;
    case "client":
      return `/clients/${id}`;
    default:
      return "/dashboard";
  }
};

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

export default function Header() {
  const profile = useSelector((state: any) => state.auth.profile);
  const permissions = useSelector((state: any) => state.auth.permissions);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: isGrafanaRunning } = useGrafanaStatus();

  const dashboardTabs = useMemo(() => {
    const base = ["Overview", "Live Tracking"];
    if (isGrafanaRunning) {
      base.push("Grafana Analytics");
    }
    return base;
  }, [isGrafanaRunning]);

  const [activeItem, setActiveItem] = useState(location.pathname);
  const [showSearch, setShowSearch] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [openResponse, setOpenResponse] = useState(false);
  const [bulkJobId, setBulkJobId] = useState<string>("");
  const [notification, setNotification] = useState<any>(null);

  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchFor, setSearchFor] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
 

  const debouncedSearch = useDebounce(searchFor, 300);
  const { refetch: profileRefetch } = useProfileData();
  const url = process.env.REACT_APP_BASE_URL;

  useEffect(() => setActiveItem(location.pathname), [location.pathname]);
    const LogoComponent =
    process.env.REACT_APP_EXTERNAL_ADMIN === "true"
      ? "/logo/logo-black.png"
      : "/img/dashboard-icons/logo-tracktech.svg";

  const handleNavClick = useCallback(
    (path: string, e?: React.MouseEvent) => {
      if (e && (e.ctrlKey || e.metaKey)) {
        window.open(path, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(path);
      setActiveItem(path);
      setShowSidebar(false);
    },
    [navigate],
  );

  const searchGlobalApi = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      setShowSearchResults(true);

      try {
        const res: any = await fetch3(
          `${url}/api/search/global?query=${encodeURIComponent(query)}`,
          "get",
        );

        const mapped = res.data.map((i: any) => ({
          ...i,
          subText: i.orderName
            ? `Order: ${i.orderName} | Status: ${i.status}`
            : i.subText,
        }));
        setSearchResults(mapped);
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch search results.");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [url],
  );

  useEffect(() => {
    searchGlobalApi(debouncedSearch);
  }, [debouncedSearch, searchGlobalApi]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchFor.trim().length >= 2) {
      setShowSearchResults(false);
      setShowSearch(false);
      navigate("/search-results", {
        state: { searchResults, searchQuery: searchFor },
      });
      setSearchFor("");
      setSearchResults([]);
    }
  };

  const handleSearchResultClick = (item: SearchResultItem) => {
    setShowSearchResults(false);
    setShowSearch(false);
    navigate(getEntityPath(item.entityType, item.entityId));
  };

  const { data, refetch } = useProfileData();
  const { mutate } = useUpdateProfilePicMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    mutate(
      { companyLogo: file },
      {
        onSuccess: () => {
          toast.success("Profile picture Updated!");
          refetch();
        },
        onError: (err: any) => {
          toast.error(err?.message ?? "Upload failed.");
        },
      },
    );
  };

  const handleLogout = async () => {
    await fetch3(`${url}/api/auth/logout?type=client`, "get");
    refetch();
    navigate("/");
  };

  const socket = useMemo(() => {
    const s = io(url!, { transports: ["websocket"] });
    s.on("notification Sent", (payload) => {
      if (payload.recipientId === profile?.id) {
        toast.success(payload.message ?? "New notification");
      }
    });
    return s;
  }, [url, profile?.id]);

  const { data: notifications, refetch: fetchNotifications } =
    useFetchAdminNotificationsData(1, 10, "");

  useEffect(() => {
    const closeNoti = (e: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(e.target as Node)
      )
        setShowNotification(false);
    };
    document.addEventListener("mousedown", closeNoti);
    return () => document.removeEventListener("mousedown", closeNoti);
  }, []);

  useEffect(() => {
    const closeProf = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node))
        setShowProfileDropdown(false);
    };
    document.addEventListener("mousedown", closeProf);
    return () => document.removeEventListener("mousedown", closeProf);
  }, []);

  const [isIpadPro, setIsIpadPro] = useState(false);

  useEffect(() => {
    const ipadProQuery = window.matchMedia(
      "(min-device-width: 1024px) and (max-device-width: 1024px) and (min-device-height: 1366px) and (max-device-height: 1366px)",
    );

    setIsIpadPro(ipadProQuery.matches);

    const listener = (e: any) => setIsIpadPro(e.matches);
    ipadProQuery.addEventListener("change", listener);

    return () => ipadProQuery.removeEventListener("change", listener);
  }, []);

  return (
    <>
      {isIpadPro ? (
        <div className="p-4 sticky top-0 z-[40]">
          <header className="flex items-center justify-between px-6 py-2 bg-[#F7F7F5]/50 backdrop-blur-md rounded-full shadow border border-white">
            <div
              onClick={() => navigate("/dashboard?tab=Overview")}
              className="flex items-end gap-2 cursor-pointer"
            >
              <img
                src="/img/dashboard-icons/logo-only.png"
                alt="Logo"
                className="max-w-[50px]"
              />
              <div
                title={profile?.type === "lmStaff" ? profile.roles : "Admin"}
                className="px-2 py-1 text-xs font-medium text-black uppercase border border-[#212121] rounded-full"
              >
                {profile?.type === "lmStaff"
                  ? profile.roles
                      ?.split(" ")
                      .map((w: string) => w[0].toUpperCase())
                      .join("")
                  : "AD"}
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative" ref={searchContainerRef}>
                <div
                  className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden border border-[#FFFFFF]/20 rounded-full cursor-pointer h-10 shadow-sm ${
                    showSearch ? "w-96 bg-[#F7F7F5]" : "w-10 bg-transparent"
                  }`}
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 text-[#212121] bg-[#F7F7F5] flex-shrink-0"
                    onClick={() => !showSearch && setShowSearch(true)}
                  >
                    <IconSearch strokeWidth={1.5} size={24} />
                  </div>

                  {showSearch && (
                    <>
                      <input
                        type="text"
                        placeholder="Search AWB, Order, User..."
                        autoFocus
                        value={searchFor}
                        onChange={(e) => setSearchFor(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        className="flex-1 h-full px-2 text-sm text-[#212121] placeholder-[#212121]/70 bg-transparent border-none outline-none"
                      />
                      <div
                        className="flex items-center justify-center w-12 h-12 text-[#212121] flex-shrink-0"
                        onClick={() => {
                          setShowSearch(false);
                          setSearchFor("");
                          setSearchResults([]);
                        }}
                      >
                        <IconX className="w-5 h-5" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div ref={notificationRef}>
                <div
                  className="relative flex items-center justify-center w-10 h-10 border border-[#FFFFFF]/20 rounded-full cursor-pointer bg-[#F7F7F5]"
                  onClick={() => {
                    fetchNotifications();
                    setShowNotification((p) => !p);
                  }}
                >
                  <IconBell
                    strokeWidth={1.5}
                    size={24}
                    className="text-[#212121]"
                  />
                  {profile?.unseenNotification ? (
                    <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                      {profile.unseenNotification}
                    </span>
                  ) : null}
                </div>

                {showNotification && (
                  <div className="absolute right-0 z-[9999] w-96 p-4 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg">
                    <h3 className="mb-2 text-lg">Notifications</h3>
                    <div className="max-h-96   overflow-y-auto">
                      {notifications?.notifications?.length ? (
                        notifications.notifications
                          .slice(0, 5)
                          .map((n: any) => (
                            <NotificationItem
                              key={n.id}
                              n={n}
                              onClick={() => {
                                if (n.title?.includes("Bulk Job Completed")) {
                                  setBulkJobId(n.referenceId);
                                  setNotification(n);
                                  setOpenResponse(true);
                                } else if (
                                  n.notification_for?.startsWith("Chat")
                                ) {
                                  navigate(
                                    `/help-and-support?chatId=${n.referenceId}`,
                                  );
                                } else {
                                  navigate("/notification-view");
                                }
                                setShowNotification(false);
                              }}
                            />
                          ))
                      ) : (
                        <p className="py-6 text-sm text-center text-gray-500">
                          No notifications
                        </p>
                      )}
                    </div>

                    <div className="pt-2 text-center border-t border-[#FFFFFF]/20">
                      <button
                        onClick={() => {
                          navigate("/notification-view");
                          setShowNotification(false);
                        }}
                        className="text-sm font-medium text-[#212121]/70 hover:text-[#FBD037] hover:underline"
                      >
                        Show More
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className={`relative ${showProfileDropdown ? "z-[9999]" : ""}`} ref={profileRef}>
                <div
                  className="flex items-center justify-center w-10 h-10 overflow-hidden rounded-full cursor-pointer"
                  onClick={() => setShowProfileDropdown((p) => !p)}
                >
                  <img
                    className="object-cover w-10 h-10 rounded-full"
                    src={data?.logo ?? "/img/dumy-profile.png"}
                    alt="Avatar"
                  />
                </div>

                {showProfileDropdown && (
                  <div className="absolute right-0 z-[9999] w-72 p-0 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg">
                    <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                      <div className="relative group">
                        <img
                          className="w-14 h-14 object-cover rounded-full"
                          src={data?.logo ?? "/img/dumy-profile.png"}
                          alt="Avatar"
                        />
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 cursor-pointer group-hover:opacity-100 transition"
                        >
                          <IconPencil size={24} color="white" />
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </div>
                      <div>
                        <h2 className="text-sm font-medium text-[#212121]">
                          {data?.username}
                        </h2>
                        <p className="text-xs text-[#212121]/70">
                          {data?.email}
                        </p>
                      </div>
                    </div>

                    <ul className="py-2 space-y-2 text-sm text-[#212121]">
                      {[
                        {
                          icon: IconUser,
                          label: "Company Info",
                          path: "/profile",
                        },
                        {
                          icon: IconBasketCog,
                          label: "Shipping Config",
                          path: "/shipping-configuration",
                        },
                        {
                          icon: IconBuilding,
                          label: "Warehouse",
                          path: "/warehouse",
                        },
                        {
                          icon: IconClipboardList,
                          label: "Subscription",
                          path: "/subscription",
                        },
                        {
                          icon: IconSettings,
                          label: "Settings",
                          path: "/settings",
                        },
                        {
                          icon: IconHistory,
                          label: "Activity Logs",
                          path: "/activity-logs",
                        },
                        {
                          icon: IconHeadset,
                          label: "Help & Support",
                          path: "/help-and-support",
                        },
                      ].map((i) => (
                        <li
                          key={i.path}
                          className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                          onClick={() => {
                            navigate(i.path);
                            setShowProfileDropdown(false);
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <i.icon stroke={1.5} />
                            {i.label}
                          </div>
                          <IconChevronRight
                            size={22}
                            stroke={1.5}
                            color="#6B7280"
                          />
                        </li>
                      ))}

                      <li
                        className="flex items-center gap-1 px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                        onClick={() => {
                          handleLogout();
                          setShowProfileDropdown(false);
                        }}
                      >
                        <IconLogout size={22} stroke={1.5} /> Logout
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </header>
        </div>
      ) : (
        <div className="sticky top-0 z-[40]">
          <div className="p-4">
            <header className="flex items-center justify-between px-6 py-2 bg-[#F7F7F5]/50 backdrop-blur-md rounded-full shadow border border-white">
              <div
                onClick={() => navigate("/dashboard")}
                className="flex items-end gap-2 cursor-pointer"
              >
                <img
                  // src="/img/dashboard-icons/logo-tracktech.svg"
                   src={LogoComponent}
                  alt="Logo"
                  className="max-w-[160px]"
                />
                <div
                  title={profile?.type === "lmStaff" ? profile.roles : "Admin"}
                  className="px-4 py-1 text-xs font-medium text-black uppercase border border-[#212121] rounded-full"
                >
                  {profile?.type === "lmStaff"
                    ? profile.roles
                        ?.split(" ")
                        .map((w: string) => w[0].toUpperCase())
                        .join("")
                    : "Admin"}
                </div>
              </div>

              {location.pathname === "/dashboard" && (
                <div className="hidden md:flex items-center justify-center mx-auto">
                  <RoleToggle
                    options={dashboardTabs}
                    active={new URLSearchParams(location.search).get("tab") || "Overview"}
                    onChange={(tab) => navigate(`/dashboard?tab=${encodeURIComponent(tab)}`)}
                  />
                </div>
              )}

              <div className="flex items-center space-x-3">
                <div className="relative" ref={searchContainerRef}>
                  <div
                    className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden border border-[#FFFFFF]/20 rounded-full cursor-pointer h-12 shadow-sm ${
                      showSearch ? "w-96 bg-[#F7F7F5]" : "w-12 bg-transparent"
                    }`}
                  >
                    <div
                      className="flex items-center justify-center w-12 h-12 text-[#212121] bg-[#F7F7F5] flex-shrink-0"
                      onClick={() => !showSearch && setShowSearch(true)}
                    >
                      <IconSearch strokeWidth={1.5} size={24} />
                    </div>

                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search AWB, Order, User..."
                          autoFocus
                          value={searchFor}
                          onChange={(e) => setSearchFor(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                          className="flex-1 h-full px-2 text-sm text-[#212121] placeholder-[#212121]/70 bg-transparent border-none outline-none"
                        />
                        <div
                          className="flex items-center justify-center w-12 h-12 text-[#212121] flex-shrink-0"
                          onClick={() => {
                            setShowSearch(false);
                            setSearchFor("");
                            setSearchResults([]);
                          }}
                        >
                          <IconX className="w-5 h-5" />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div ref={notificationRef}>
                  <div
                    className="relative flex items-center justify-center w-12 h-12 border border-[#FFFFFF]/20 rounded-full cursor-pointer bg-[#F7F7F5]"
                    onClick={() => {
                      fetchNotifications();
                      setShowNotification((p) => !p);
                    }}
                  >
                    <IconBell
                      strokeWidth={1.5}
                      size={24}
                      className="text-[#212121]"
                    />
                    {profile?.unseenNotification ? (
                      <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                        {profile.unseenNotification}
                      </span>
                    ) : null}
                  </div>

                  {showNotification && (
                    <div className="absolute right-0 z-[9999] w-96 p-4 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg">
                      <h3 className="mb-2 text-lg">Notifications</h3>
                      <div className="max-h-96   overflow-y-auto">
                        {notifications?.notifications?.length ? (
                          notifications.notifications
                            .slice(0, 5)
                            .map((n: any) => (
                              <NotificationItem
                                key={n.id}
                                n={n}
                                onClick={() => {
                                  if (n.title?.includes("Bulk Job Completed")) {
                                    setBulkJobId(n.referenceId);
                                    setNotification(n);
                                    setOpenResponse(true);
                                  } else if (
                                    n.notification_for?.startsWith("Chat")
                                  ) {
                                    navigate(
                                      `/help-and-support?chatId=${n.referenceId}`,
                                    );
                                  } else {
                                    navigate("/notification-view");
                                  }
                                  setShowNotification(false);
                                }}
                              />
                            ))
                        ) : (
                          <p className="py-6 text-sm text-center text-gray-500">
                            No notifications
                          </p>
                        )}
                      </div>

                      <div className="pt-2 text-center border-t border-[#FFFFFF]/20">
                        <button
                          onClick={() => {
                            navigate("/notification-view");
                            setShowNotification(false);
                          }}
                          className="text-sm font-medium text-[#212121]/70 hover:text-[#FBD037] hover:underline"
                        >
                          Show More
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`relative ${showProfileDropdown ? "z-[9999]" : ""}`} ref={profileRef}>
                  <div
                    className="flex items-center justify-center w-12 h-12 overflow-hidden rounded-full cursor-pointer"
                    onClick={() => setShowProfileDropdown((p) => !p)}
                  >
                    <img
                      className="object-cover w-12 h-12 rounded-full"
                      src={data?.logo ?? "/img/dumy-profile.png"}
                      alt="Avatar"
                    />
                  </div>

                  {showProfileDropdown && (
                    <div className="absolute right-0 z-[9999] w-72 p-0 mt-2 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg">
                      <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                        <div className="relative group">
                          <img
                            className="w-14 h-14 object-cover rounded-full"
                            src={data?.logo ?? "/img/dumy-profile.png"}
                            alt="Avatar"
                          />
                          <div
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 cursor-pointer group-hover:opacity-100 transition"
                          >
                            <IconPencil size={24} color="white" />
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileChange}
                          />
                        </div>
                        <div>
                          <h2 className="text-sm font-medium text-[#212121]">
                            {data?.username}
                          </h2>
                          <p className="text-xs text-[#212121]/70">
                            {data?.email}
                          </p>
                        </div>
                      </div>

                      <ul className="py-2 space-y-2 text-sm text-[#212121]">
                        {[
                          {
                            icon: IconUser,
                            label: "Company Info",
                            path: "/profile",
                          },
                        ].map((i) => (
                          <li
                            key={i.path}
                            className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                            onClick={() => {
                              navigate(i.path);
                              setShowProfileDropdown(false);
                            }}
                          >
                            <div className="flex items-center gap-1">
                              <i.icon stroke={1.5} />
                              {i.label}
                            </div>
                            <IconChevronRight
                              size={22}
                              stroke={1.5}
                              color="#6B7280"
                            />
                          </li>
                        ))}

                        <li
                          className="flex items-center gap-1 px-4 py-2 cursor-pointer hover:bg-[#F7F6F2]"
                          onClick={() => {
                            handleLogout();
                            setShowProfileDropdown(false);
                          }}
                        >
                          <IconLogout size={22} stroke={1.5} /> Logout
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </header>
          </div>
          {openResponse && (
            <BulkResponseHandler
              isOpen={openResponse}
              onClose={() => setOpenResponse(false)}
              bulkJobId={bulkJobId}
              notification={notification}
            />
          )}
        </div>
      )}
    </>
  );
}
